import React, { useState } from "react";
import axios from "axios";
import { Lock, Users, Inbox, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("pm_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    setErr("");
    try {
      const headers = { "X-Admin-Token": t };
      const [a, s] = await Promise.all([
        axios.get(`${API}/applications`, { headers }),
        axios.get(`${API}/applications/stats`, { headers }),
      ]);
      setApps(a.data);
      setStats(s.data);
      setAuthed(true);
      localStorage.setItem("pm_admin", t);
    } catch (e) {
      setErr("Nieprawidłowy token administratora.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć to zgłoszenie na stałe? Tej operacji nie można cofnąć.")) return;
    try {
      await axios.delete(`${API}/applications/${id}`, { headers: { "X-Admin-Token": token } });
      setApps((prev) => prev.filter((a) => a.id !== id));
      setStats((s) => ({ ...s, total: Math.max(0, (s.total || 0) - 1) }));
    } catch (e) {
      alert("Nie udało się usunąć zgłoszenia. Spróbuj ponownie.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-graphite flex items-center justify-center px-4">
        <div className="pm-card p-8 w-full max-w-sm" data-testid="admin-login">
          <Lock className="text-coral mb-4" size={28} />
          <h1 className="font-display text-2xl text-white mb-2">Panel PMPOINT</h1>
          <p className="text-sm text-white/55 mb-6">Wpisz token administratora, aby zobaczyć zgłoszenia.</p>
          <input
            data-testid="admin-token-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Token administratora"
            className="w-full h-12 px-4 rounded-lg bg-graphite/70 border border-white/12 text-white focus:border-coral focus:outline-none mb-3"
          />
          {err && <p className="text-sm text-red-400 mb-3" data-testid="admin-error">{err}</p>}
          <button
            data-testid="admin-login-btn"
            onClick={() => load(token)}
            disabled={loading}
            className="w-full h-12 rounded-lg bg-coral text-white font-bold hover:bg-coral-hover transition-colors disabled:opacity-60"
          >
            {loading ? "Sprawdzam..." : "Zaloguj"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-graphite text-white p-6 md:p-10">
      <div className="container-pm">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl">Zgłoszenia</h1>
          <button onClick={() => load(token)} className="flex items-center gap-2 text-sm text-white/60 hover:text-coral" data-testid="admin-refresh">
            <RefreshCw size={16} /> Odśwież
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-8 max-w-md">
          <div className="pm-card p-5 flex items-center gap-3">
            <Users className="text-coral" /> <div><div className="text-2xl font-bold font-display">{stats.total}</div><div className="text-xs text-white/50">Wszystkie</div></div>
          </div>
          <div className="pm-card p-5 flex items-center gap-3">
            <Inbox className="text-gold" /> <div><div className="text-2xl font-bold font-display">{stats.new}</div><div className="text-xs text-white/50">Nowe</div></div>
          </div>
        </div>

        <div className="pm-card overflow-x-auto" data-testid="admin-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/50 border-b border-white/10">
                <th className="p-4 font-medium">Imię</th>
                <th className="p-4 font-medium">Wiek</th>
                <th className="p-4 font-medium">Miasto</th>
                <th className="p-4 font-medium">Kontakt</th>
                <th className="p-4 font-medium">Model</th>
                <th className="p-4 font-medium">O sobie</th>
                <th className="p-4 font-medium">Data</th>
                <th className="p-4 font-medium text-right">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {apps.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-white/40">Brak zgłoszeń.</td></tr>
              )}
              {apps.map((a) => (
                <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4 font-semibold">{a.name}</td>
                  <td className="p-4">{a.age}</td>
                  <td className="p-4">{a.city}</td>
                  <td className="p-4 text-coral">{a.contact}</td>
                  <td className="p-4">{a.mode === "image" ? "Wizerunek" : "Anonimowo"}</td>
                  <td className="p-4 text-white/60 max-w-[220px] truncate">{a.about || a.platforms_experience || "—"}</td>
                  <td className="p-4 text-white/40 whitespace-nowrap">{new Date(a.created_at).toLocaleString("pl-PL")}</td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => remove(a.id)}
                      data-testid={`admin-delete-${a.id}`}
                      title="Usuń zgłoszenie"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/12 text-white/60 hover:bg-red-500 hover:border-red-500 hover:text-white transition-colors"
                    >
                      <Trash2 size={15} /> Usuń
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
